import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  getAllPayouts,
  processCreatorPayout,
  PayoutQueueItem,
} from '../adminApi';

type StatusFilter = 'pending' | 'completed' | 'all';

const PayoutQueue: React.FC = () => {
  const [payouts, setPayouts] = useState<PayoutQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [actionLoading, setActionLoading] = useState(false);

  // Process dialog
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<PayoutQueueItem | null>(null);
  const [paymentReference, setPaymentReference] = useState('');

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAllPayouts({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setPayouts(result || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleProcess = async () => {
    if (!selectedPayout || !paymentReference.trim()) return;

    setActionLoading(true);
    setError(null);
    try {
      await processCreatorPayout(selectedPayout.payout_id, paymentReference.trim());
      setSuccessMessage(`Payout #${selectedPayout.payout_id} for ${selectedPayout.creator_name} marked as paid`);
      setProcessDialogOpen(false);
      setSelectedPayout(null);
      setPaymentReference('');
      fetchPayouts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process payout');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toFixed(2)}`;
  };

  const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    const colors: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'error',
    };
    return colors[status] || 'default';
  };

  const parsePaymentDetails = (details: string | null): string => {
    if (!details) return 'Not configured';
    try {
      const parsed = JSON.parse(details);
      if (parsed.paypal_email) return parsed.paypal_email;
      if (parsed.email) return parsed.email;
      return JSON.stringify(parsed);
    } catch {
      return details;
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Payout Queue
        </Typography>
      </Stack>

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          sx={{ px: 2 }}
        >
          <Tab label="Pending" value="pending" />
          <Tab label="Completed" value="completed" />
          <Tab label="All" value="all" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Creator</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : payouts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {statusFilter === 'pending' ? 'No pending payouts' : 'No payouts found'}
                </TableCell>
              </TableRow>
            ) : (
              payouts.map((payout) => (
                <TableRow key={payout.payout_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {payout.creator_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {payout.creator_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(payout.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(payout.period_start)} - {formatDate(payout.period_end)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payout.creator_payment_method || payout.payment_method || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {parsePaymentDetails(payout.creator_payment_details)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payout.status}
                      size="small"
                      color={getStatusColor(payout.status)}
                    />
                  </TableCell>
                  <TableCell>{formatDate(payout.requested_at || payout.created_at)}</TableCell>
                  <TableCell>
                    {(payout.status === 'pending' || payout.status === 'processing') && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setSelectedPayout(payout);
                          setProcessDialogOpen(true);
                        }}
                        disabled={actionLoading}
                      >
                        Process
                      </Button>
                    )}
                    {payout.status === 'completed' && payout.payment_reference && (
                      <Typography variant="caption" color="text.secondary">
                        Ref: {payout.payment_reference}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Process Payout Dialog */}
      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Payout</DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Creator</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedPayout.creator_name}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(selectedPayout.amount)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                    <Typography variant="body2">{selectedPayout.creator_payment_method || selectedPayout.payment_method || 'N/A'}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Pay To</Typography>
                    <Typography variant="body2">{parsePaymentDetails(selectedPayout.creator_payment_details)}</Typography>
                  </Stack>
                </Stack>
              </Paper>
              <TextField
                label="PayPal Transaction ID / Payment Reference"
                fullWidth
                required
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="e.g., PAYPAL-TXN-12345"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setProcessDialogOpen(false);
            setPaymentReference('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleProcess}
            disabled={actionLoading || !paymentReference.trim()}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutQueue;
